import { useEffect, useRef } from "react";

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const isVisible = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none" && el.getClientRects().length > 0;
};

const getFocusable = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll(focusableSelectors)).filter(
    (el) => isVisible(el) && !el.hasAttribute("aria-hidden")
  );
};

const activeTraps = [];
let originalBodyOverflow = "";

export function useFocusTrap({ containerRef, open, initialFocusRef, onClose }) {
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (activeTraps.length === 0) originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const container = containerRef?.current;
    activeTraps.push(container);

    const moveInitialFocus = () => {
      if (activeTraps[activeTraps.length - 1] !== container) return;
      const target = initialFocusRef?.current || getFocusable(container)[0] || container;
      target?.focus?.();
    };

    const handleKeyDown = (e) => {
      if (!container || activeTraps[activeTraps.length - 1] !== container) return;

      if (e.key === "Escape" && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (e.key !== "Tab") return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const isInside = container.contains(active);

      if (e.shiftKey) {
        if (!isInside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!isInside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const timer = window.setTimeout(moveInitialFocus, 0);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", handleKeyDown);
      const wasTop = activeTraps[activeTraps.length - 1] === container;
      activeTraps.splice(activeTraps.indexOf(container), 1);
      if (activeTraps.length === 0) document.body.style.overflow = originalBodyOverflow;
      const prev = previousFocusRef.current;
      if (wasTop && prev?.isConnected && prev?.focus) {
        prev.focus();
      }
    };
  }, [containerRef, initialFocusRef, open]);
}

export default useFocusTrap;
